import React, { useEffect, useState, useCallback } from "react";
import { Box, Grid, Typography, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PaginatedTable from "../Common/Tables/table";
import Dialog from "../Common/Dialog";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";
import request from "../../request/request";
import { getAllSystemPrompts } from "../../features/systemPromptDetails/systemPromptDetailsAction";
import ConfirmDialog from "../Common/ConfirmDialog";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import SystemPromptFormDialog from "./SystemPromptForm";

const SystemPrompt = () => {
  const dispatch = useDispatch();
  const { data, total, loading } = useSelector(
    (state) => state.systemPromptDetail
  );
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [pageOffset, setPageOffset] = useState(0);
  const [openAddPromptDialog, setOpenAddPromptDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);

  const categoryOptions = [
    { value: "accounts", label: "Accounts" },
    { value: "contacts", label: "Contacts" },
    { value: "meetings", label: "Meetings" },
    { value: "opportunities", label: "Opportunities" },
  ];

  const fetchSystemPrompts = useCallback(
    (params = {}) => {
      const {
        limit: l = limit,
        offset = pageOffset,
        search = searchText,
      } = params;
      const query = { limit: l, offset };
      if (search?.trim()) {
        query.search = search.trim();
        query.fields = JSON.stringify(["name", "category"]);
      }
      dispatch(getAllSystemPrompts(query));
    },
    [limit, pageOffset, searchText, dispatch]
  );

  useEffect(() => {
    fetchSystemPrompts();
  }, [fetchSystemPrompts]);

  const validationSchema = Yup.object({
    name: Yup.string().required("System Prompt Name is required"),
    category: Yup.array()
      .min(1, "At least one category is required")
      .of(
        Yup.object().shape({
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
      ),
  });

  const { reset } = useForm({
    defaultValues: { name: "", category: [], prompt: "" },
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        category: values.category.map((c) =>
          typeof c === "string" ? c : c.value
        ),
      };
      const endpoint = isEditMode
        ? `/api/v1/system-prompts/update/${editingPrompt?.uuid}`
        : "/api/v1/system-prompts/create";
      const method = isEditMode ? "PUT" : "POST";
      const response = await request(endpoint, {
        method,
        body: payload,
        auth: true,
      });
      console.log(
        `System Prompt ${isEditMode ? "updated" : "created"} successfully`,
        response
      );
      fetchSystemPrompts();
      handleCloseFormDialog();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} prompt:`,
        error
      );
    }
  };

  const handleAddPrompt = () => setOpenAddPromptDialog(true);

  const handleCloseFormDialog = () => {
    reset();
    setOpenAddPromptDialog(false);
    setIsEditMode(false);
    setEditingPrompt(null);
  };

  const handleEditPrompt = (row) => {
    const normalizedCategory = Array.isArray(row.category)
      ? row.category.map((cat) =>
          typeof cat === "string"
            ? categoryOptions.find((opt) => opt.value === cat) || {
                value: cat,
                label: cat,
              }
            : cat
        )
      : row.category
      ? [
          typeof row.category === "string"
            ? { value: row.category, label: row.category }
            : row.category,
        ]
      : [];
    setEditingPrompt(row);
    setIsEditMode(true);
    reset({
      name: row.name || "",
      category: normalizedCategory,
      prompt: row.prompt || "",
    });
    setOpenAddPromptDialog(true);
  };

  const handleDeletePrompt = async () => {
    if (!promptToDelete) return;
    try {
      const response = await request(
        `/api/v1/system-prompts/delete/${promptToDelete?.uuid}`,
        {
          method: "DELETE",
          auth: true,
        }
      );
      console.log("System Prompt deleted successfully", response);
      fetchSystemPrompts();
      setOpenDeleteDialog(false);
      setPromptToDelete(null);
    } catch (error) {
      console.error("Error deleting prompt:", error);
    }
  };

  const columns = [
    { Header: "System Prompt Version", accessor: "name" },
    {
      Header: "Category",
      accessor: "category",
      Cell: ({ value }) =>
        Array.isArray(value)
          ? value
              .map((item) => (typeof item === "string" ? item : item.label))
              .join(", ")
          : value?.label || value,
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      Header: "Updated At",
      accessor: "updatedAt",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      Header: "Actions",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="View System Prompt">
            <IconButton
              color="info"
              onClick={() => {
                setViewingPrompt(row.original);
                setOpenViewDialog(true);
              }}
            >
              <IconEye size="1.8rem" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => handleEditPrompt(row.original)}
            >
              <IconPencil size="1.8rem" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={() => {
                setPromptToDelete(row.original);
                setOpenDeleteDialog(true);
              }}
            >
              <IconTrash size="1.8rem" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const normalizeCategory = (catList) => {
    if (!Array.isArray(catList)) return [];
    return catList.map((cat) =>
      typeof cat === "string"
        ? categoryOptions.find((opt) => opt.value === cat) || {
            value: cat,
            label: cat,
          }
        : cat
    );
  };

  return (
    <Box sx={{ width: "100%", padding: "10px", marginTop: "65px" }}>
      <PaginatedTable
        columns={columns}
        data={data || []}
        totalCount={total || 0}
        heading="System Prompt List"
        headingButtonText="Add System Prompt"
        headingBtnClick={handleAddPrompt}
        paginated
        pageOffset={pageOffset}
        limit={limit}
        isFetching={loading}
        setLimitFromParents={setLimit}
        setPageOffsetFromParents={setPageOffset}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
          setPageOffset(0);
        }}
        showSearch
      />
      <SystemPromptFormDialog
        key={isEditMode ? editingPrompt?.uuid : "new"}
        open={openAddPromptDialog}
        isEditMode={isEditMode}
        onClose={handleCloseFormDialog}
        onSuccess={onSubmit}
        defaultValues={{
          name: editingPrompt?.name || "",
          category: editingPrompt
            ? normalizeCategory(editingPrompt.category)
            : [],
          prompt: editingPrompt?.prompt || "",
        }}
        categoryOptions={categoryOptions}
      />

      <Dialog
        isOpen={openViewDialog}
        handleClose={() => setOpenViewDialog(false)}
        cancelSubmitHandle={() => setOpenViewDialog(false)}
        hideOkBtn
        title={<Typography variant="h6">System Prompt Content</Typography>}
      >
        {viewingPrompt && (
          <Box
            sx={{
              padding: 2,
              border: "1px solid #ccc",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9",
              overflowY: "auto",
              maxHeight: "400px",
            }}
            dangerouslySetInnerHTML={{ __html: viewingPrompt.prompt }}
          />
        )}
      </Dialog>
      <ConfirmDialog
        open={openDeleteDialog}
        title={`Are you sure you want to delete "${promptToDelete?.name}"?`}
        onConfirm={handleDeletePrompt}
        onCancel={() => setOpenDeleteDialog(false)}
      />
    </Box>
  );
};

export default SystemPrompt;
